import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { Text } from '@lifeomic/chroma-react/components/Text';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import { DocumentReferenceAttachment } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/Attachment';
import { DocumentReferenceContent } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/modals/DocumentReferenceModal';
import type {
  DocumentExtractionV2Invocation,
  DocumentExtractionV2Output,
  DocumentExtractionV2Status,
} from '@lifeomic/react-client';
import {
  useActiveAccountId,
  useActiveProjectId,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useApiClient } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import {
  getObservationMMMYYYYDate,
  getObservationUnit,
  getObservationValueDisplay,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/util/fhir';
import {
  ExtractionFeedbackActions,
  withApprovedExtractionExtensions,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/Graphs/Ranges/ExtractionFeedbackActions';
import AuditMasterDetail from '../../AIRules/components/AuditMasterDetail';
import { formatDateTime } from '../../AIRules/utils';
import { memberDetailMessages } from './messages';

const IN_FLIGHT_STATUSES: DocumentExtractionV2Status[] = [
  'scheduled',
  'pending',
  'processing',
  'waiting',
];

const NO_FAILURE_REASON_MESSAGE =
  'No failure reason was recorded for this task.';

const SOURCE_PREVIEW_HEIGHT = 420;

const documentReferenceFhirService = new FhirService('DocumentReference');
const communicationFhirService = new FhirService('Communication');
const observationFhirService = new FhirService('Observation');

type ExtendedDocumentExtractionV2Output = DocumentExtractionV2Output & {
  source_resource_type?: string | null;
  source_resource_id?: string | null;
};

type ExtendedDocumentExtractionV2Invocation = DocumentExtractionV2Invocation & {
  source_resource_type?: string | null;
  source_resource_id?: string | null;
  output?: ExtendedDocumentExtractionV2Output | null;
};

type SourceResource = {
  resourceType: string;
  id: string;
};

export const isDocumentExtractionV2InFlight = (
  task: DocumentExtractionV2Invocation,
) => IN_FLIGHT_STATUSES.includes(task.status);

export const formatDocumentExtractionV2Status = (
  status: DocumentExtractionV2Status,
) => status.charAt(0).toUpperCase() + status.slice(1);

const getTaskSourceResource = (
  task: DocumentExtractionV2Invocation,
): SourceResource | null => {
  const extendedTask = task as ExtendedDocumentExtractionV2Invocation;
  const sourceResourceType =
    extendedTask.source_resource_type ??
    extendedTask.output?.source_resource_type;
  const sourceResourceId =
    extendedTask.source_resource_id ?? extendedTask.output?.source_resource_id;

  if (sourceResourceType && sourceResourceId) {
    return {
      resourceType: sourceResourceType,
      id: sourceResourceId,
    };
  }

  const sourceDocumentReferenceId =
    task.source_document_reference_id ??
    task.output?.source_document_reference_id;

  if (!sourceDocumentReferenceId) {
    return null;
  }

  return {
    resourceType: 'DocumentReference',
    id: sourceDocumentReferenceId.replace(/^DocumentReference\//, ''),
  };
};

const getSourceLabel = (
  task: DocumentExtractionV2Invocation,
  highlightedSourceDocumentReferenceId: string | undefined,
  thisDocumentLabel: string,
) => {
  const sourceResource = getTaskSourceResource(task);

  if (!sourceResource) {
    return '';
  }

  if (
    sourceResource.resourceType === 'DocumentReference' &&
    sourceResource.id === highlightedSourceDocumentReferenceId
  ) {
    return thisDocumentLabel;
  }

  if (sourceResource.resourceType === 'DocumentReference') {
    return sourceResource.id;
  }

  return `${sourceResource.resourceType}/${sourceResource.id}`;
};

const getFailureReason = (task: DocumentExtractionV2Invocation) => {
  const failureReason = (
    task.output as { failure_reason?: string | null } | null | undefined
  )?.failure_reason?.trim();

  return failureReason || NO_FAILURE_REASON_MESSAGE;
};

type DocumentExtractionV2Resource = (
  | NonNullable<
      NonNullable<DocumentExtractionV2Output['fhir_projections']>[number]
    >
  | NonNullable<
      NonNullable<DocumentExtractionV2Output['fhir_artifacts']>[number]
    >
) & {
  dedupe_outcome?: string | null;
  fact_type?: string | null;
  matched_resource_id?: string | null;
  reason?: string | null;
  role?: string | null;
};

const isObservationProjection = (resource: DocumentExtractionV2Resource) =>
  resource.resource_type === 'Observation' && !!resource.resource_id;

const getPersistedObservationResourceIds = (
  task: DocumentExtractionV2Invocation,
) => {
  const output = task.output;
  const fhirProjections = output?.fhir_projections;
  const projections = Array.isArray(fhirProjections) ? fhirProjections : [];
  const fhirArtifacts = output?.fhir_artifacts;
  const artifacts = Array.isArray(fhirArtifacts) ? fhirArtifacts : [];
  const ids = [
    ...projections
      .filter(
        projection =>
          isObservationProjection(projection) &&
          ['accepted-new', 'matched-existing'].includes(
            projection.status ?? '',
          ),
      )
      .map(projection => projection.resource_id!),
    ...artifacts
      .filter(
        artifact =>
          isObservationProjection(artifact) && artifact.status === 'persisted',
      )
      .map(artifact => artifact.resource_id!),
  ];

  return Array.from(new Set(ids));
};

const getResourceLabel = (
  resource: DocumentExtractionV2Resource,
  fallback: string,
) => resource.resource_type ?? fallback;

const getResourceDetails = (resource: DocumentExtractionV2Resource) =>
  [
    resource.status ? `Status: ${resource.status}` : null,
    resource.dedupe_outcome
      ? `Dedupe outcome: ${resource.dedupe_outcome}`
      : null,
    resource.matched_resource_id
      ? `Matched resource ID: ${resource.matched_resource_id}`
      : null,
    resource.fact_type ? `Fact type: ${resource.fact_type}` : null,
    resource.role ? `Role: ${resource.role}` : null,
    resource.reason ? `Reason: ${resource.reason}` : null,
    resource.resource_id ? `Resource ID: ${resource.resource_id}` : null,
  ].filter((detail): detail is string => !!detail);

const getExtractedResourceItems = (task: DocumentExtractionV2Invocation) => {
  const output = task.output;
  const fhirProjections = output?.fhir_projections;
  const projections = Array.isArray(fhirProjections) ? fhirProjections : [];
  const fhirArtifacts = output?.fhir_artifacts;
  const artifacts = Array.isArray(fhirArtifacts)
    ? fhirArtifacts.filter(
        artifact => artifact.role !== 'summary-document-reference',
      )
    : [];

  return [
    ...projections.map((projection, index) => ({
      key: `projection-${index}-${projection.resource_id ?? 'pending'}`,
      label: getResourceLabel(projection, 'FHIR projection'),
      details: getResourceDetails(projection),
    })),
    ...artifacts.map((artifact, index) => ({
      key: `artifact-${index}-${artifact.resource_id ?? 'pending'}`,
      label: getResourceLabel(artifact, 'FHIR artifact'),
      details: getResourceDetails(artifact),
    })),
  ];
};

const getDocumentTitle = (
  document: fhir.DocumentReference | null,
  fallback: string,
) =>
  document?.type?.text ||
  document?.description ||
  document?.content?.[0]?.attachment?.title ||
  document?.id ||
  fallback;

const getCommunicationTitle = (communication: fhir.Communication | null) =>
  communication?.category?.[0]?.text ||
  communication?.reasonCode?.[0]?.text ||
  communication?.id ||
  'Source communication';

const getCommunicationContent = (communication: fhir.Communication | null) =>
  (communication?.payload?.[0] as { contentString?: string } | undefined)
    ?.contentString ||
  communication?.note?.[0]?.text ||
  '';

const getObservationDisplayName = (observation: fhir.Observation) =>
  observation.code?.text ||
  observation.code?.coding?.find(coding => !!coding.display)?.display ||
  observation.code?.coding?.find(coding => !!coding.code)?.code ||
  'Observation';

const getExtractedObservationValueDisplay = (observation: fhir.Observation) =>
  [getObservationValueDisplay(observation), getObservationUnit(observation)]
    .filter(Boolean)
    .join(' ');

const DocumentExtractionTaskSourcePreview: React.FC<{
  task: DocumentExtractionV2Invocation;
}> = ({ task }) => {
  const { formatMessage } = useIntl();
  const accountId = useActiveAccountId();
  const serviceAuth = useServiceAuth();
  const sourceResource = getTaskSourceResource(task);
  const [sourceDocument, setSourceDocument] =
    useState<fhir.DocumentReference | null>(null);
  const [sourceCommunication, setSourceCommunication] =
    useState<fhir.Communication | null>(null);
  const [isLoadingSource, setIsLoadingSource] = useState(false);
  const [sourceErrorMessage, setSourceErrorMessage] = useState<string | null>(
    null,
  );
  const sourceResourceType = sourceResource?.resourceType;
  const sourceResourceId = sourceResource?.id;

  useEffect(() => {
    let isCancelled = false;

    const loadSource = async () => {
      setSourceDocument(null);
      setSourceCommunication(null);
      setSourceErrorMessage(null);

      if (
        !sourceResourceType ||
        !sourceResourceId ||
        !accountId ||
        !serviceAuth
      ) {
        return;
      }

      setIsLoadingSource(true);
      try {
        if (sourceResourceType === 'DocumentReference') {
          const response = await documentReferenceFhirService.getById(
            serviceAuth,
          )(accountId, sourceResourceId);
          if (!isCancelled) {
            setSourceDocument(response.data);
          }
        } else if (sourceResourceType === 'Communication') {
          const response = await communicationFhirService.getById(serviceAuth)(
            accountId,
            sourceResourceId,
          );
          if (!isCancelled) {
            setSourceCommunication(response.data);
          }
        }
      } catch {
        if (!isCancelled) {
          setSourceErrorMessage(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionSourceLoadError,
            ),
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingSource(false);
        }
      }
    };

    void loadSource();

    return () => {
      isCancelled = true;
    };
  }, [
    accountId,
    formatMessage,
    serviceAuth,
    sourceResourceId,
    sourceResourceType,
  ]);

  if (!sourceResource) {
    return (
      <Text>
        {formatMessage(
          memberDetailMessages.memberDocumentsExtractionProvenanceUnknownSourceDocument,
        )}
      </Text>
    );
  }

  if (isLoadingSource) {
    return <Text>Loading source...</Text>;
  }

  if (sourceErrorMessage) {
    return <Text color="negative">{sourceErrorMessage}</Text>;
  }

  if (sourceResource.resourceType === 'Communication') {
    return (
      <Box direction="column" gap={1}>
        <Text>{getCommunicationTitle(sourceCommunication)}</Text>
        <Box
          padding={1}
          style={{
            border: '1px solid var(--chroma-palette-divider)',
            maxHeight: SOURCE_PREVIEW_HEIGHT,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          <Text>{getCommunicationContent(sourceCommunication)}</Text>
        </Box>
      </Box>
    );
  }

  const attachmentUrl = sourceDocument?.content?.[0]?.attachment?.url;
  const sourceDocumentAttachment = sourceDocument
    ? new DocumentReferenceAttachment(sourceDocument)
    : null;

  return (
    <Box direction="column" gap={1}>
      <Text>
        {getDocumentTitle(
          sourceDocument,
          sourceResource.id ||
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionUnknownDocument,
            ),
        )}
      </Text>
      {sourceDocumentAttachment && attachmentUrl ? (
        <Box
          style={{
            border: '1px solid var(--chroma-palette-divider)',
            height: SOURCE_PREVIEW_HEIGHT,
            overflow: 'hidden',
          }}
          data-testid="document-extraction-v2-task-source-document"
        >
          <DocumentReferenceContent
            attachment={sourceDocumentAttachment}
            reference={sourceDocument!}
            disableDeepLink
            disablePdfNav
          />
        </Box>
      ) : (
        <Text>
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionProvenanceUnknownSourceDocument,
          )}
        </Text>
      )}
    </Box>
  );
};

const DocumentExtractionTaskObservations: React.FC<{
  task: DocumentExtractionV2Invocation;
  subjectId: string;
}> = ({ task, subjectId }) => {
  const { formatMessage } = useIntl();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const serviceAuth = useServiceAuth();
  const [observations, setObservations] = useState<fhir.Observation[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const observationResourceIds = useMemo(
    () => getPersistedObservationResourceIds(task),
    [task],
  );
  const observationResourceIdsKey = observationResourceIds.join('|');

  useEffect(() => {
    let isCancelled = false;

    const loadObservations = async () => {
      if (!accountId || !serviceAuth || !observationResourceIds.length) {
        setObservations([]);
        setErrorMessage(null);
        return;
      }

      try {
        const response = await observationFhirService.getByIds(serviceAuth)(
          accountId,
          observationResourceIds,
        );
        if (!isCancelled) {
          setObservations(
            response.data.entry
              ?.map(entry => entry.resource)
              .filter(
                (resource): resource is fhir.Observation =>
                  resource.resourceType === 'Observation',
              ) ?? [],
          );
          setErrorMessage(null);
        }
      } catch {
        if (!isCancelled) {
          setObservations([]);
          setErrorMessage(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionObservationsLoadError,
            ),
          );
        }
      }
    };

    void loadObservations();

    return () => {
      isCancelled = true;
    };
  }, [
    accountId,
    formatMessage,
    observationResourceIds,
    observationResourceIdsKey,
    serviceAuth,
  ]);

  if (!observationResourceIds.length && !errorMessage) {
    return null;
  }

  return (
    <Box direction="column" gap={1}>
      <Text weight="bold">
        {formatMessage(
          memberDetailMessages.memberDocumentsExtractionObservations,
        )}
      </Text>
      {errorMessage ? <Text color="negative">{errorMessage}</Text> : null}
      {observations.map(observation => (
        <Box key={observation.id} direction="column" gap={0.5}>
          <Text>{getObservationDisplayName(observation)}</Text>
          <Text size="caption">
            {[
              getExtractedObservationValueDisplay(observation),
              getObservationMMMYYYYDate(observation),
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
          {observation.id ? (
            <>
              <Text size="caption">
                {formatMessage(
                  memberDetailMessages.memberDocumentsExtractionObservationResourceId,
                  { id: observation.id },
                )}
              </Text>
              <ExtractionFeedbackActions
                observation={observation}
                subjectId={subjectId}
                projectId={projectId}
                taskId={task.task_id}
                onApproved={response => {
                  if (response.approved_by && response.approved_at) {
                    setObservations(current =>
                      current.map(currentObservation =>
                        currentObservation.id === response.observation_id
                          ? withApprovedExtractionExtensions(
                              currentObservation,
                              {
                                approvedBy: response.approved_by!,
                                approvedAt: response.approved_at!,
                              },
                            )
                          : currentObservation,
                      ),
                    );
                  }
                }}
                onRejected={observationId => {
                  setObservations(current =>
                    current.filter(
                      currentObservation =>
                        currentObservation.id !== observationId,
                    ),
                  );
                }}
              />
            </>
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

const DocumentExtractionTaskDetail: React.FC<{
  task: DocumentExtractionV2Invocation;
  subjectId: string;
  highlightedSourceDocumentReferenceId?: string;
}> = ({ task, subjectId, highlightedSourceDocumentReferenceId }) => {
  const { formatMessage } = useIntl();
  const output = task.output;
  const sourceLabel = getSourceLabel(
    task,
    highlightedSourceDocumentReferenceId,
    formatMessage(memberDetailMessages.memberDocumentsExtractionThisDocument),
  );
  const resourceItems = getExtractedResourceItems(task);

  return (
    <Box direction="column" gap={2} padding={3}>
      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionSourceDocument,
          )}
        </Text>
        <Text size="caption">{sourceLabel}</Text>
        <DocumentExtractionTaskSourcePreview task={task} />
      </Box>

      <Divider />

      <DocumentExtractionTaskObservations task={task} subjectId={subjectId} />

      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionResources,
          )}
        </Text>
        {resourceItems.length ? (
          resourceItems.map(resource => (
            <Box key={resource.key} direction="column" gap={0.5}>
              <Text>{resource.label}</Text>
              {resource.details.map(detail => (
                <Text key={detail} size="caption">
                  {detail}
                </Text>
              ))}
            </Box>
          ))
        ) : (
          <Text>
            {formatMessage(
              memberDetailMessages.memberDocumentsExtractionResourcesEmpty,
            )}
          </Text>
        )}
      </Box>

      <Divider />

      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(memberDetailMessages.memberDocumentsExtractionStatus)}
        </Text>
        <Text>
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionStatusValue,
            { status: formatDocumentExtractionV2Status(task.status) },
          )}
        </Text>
        {task.status === 'failed' ? (
          <Text color="negative">{getFailureReason(task)}</Text>
        ) : null}
        <Text size="caption">Task ID: {task.task_id}</Text>
        <Text size="caption">Created: {formatDateTime(task.created)}</Text>
        <Text size="caption">
          AI summary available: {output?.summary_available ? 'Yes' : 'No'}
        </Text>
        <Text size="caption">
          Pages reviewed: {String(output?.num_pages ?? 0)}
        </Text>
        <Text size="caption">
          Candidate facts: {String(output?.candidate_fact_count ?? 0)}
        </Text>
        <Text size="caption">Errors: {String(output?.error_count ?? 0)}</Text>
        <Text size="caption">
          Artifact version: {output?.artifact_version ?? 'pending'}
        </Text>
      </Box>
    </Box>
  );
};

type DocumentExtractionV2TasksProps = {
  subjectId: string;
  highlightedSourceDocumentReferenceId?: string;
};

type DocumentExtractionV2TasksState = {
  tasks: DocumentExtractionV2Invocation[];
  selectedTaskId: string | null;
};

export const DocumentExtractionV2Tasks: React.FC<
  DocumentExtractionV2TasksProps
> = ({ subjectId, highlightedSourceDocumentReferenceId }) => {
  const { formatMessage } = useIntl();
  const apiClient = useApiClient();
  const projectId = useActiveProjectId();
  const [{ tasks, selectedTaskId }, setTaskState] =
    useState<DocumentExtractionV2TasksState>({
      tasks: [],
      selectedTaskId: null,
    });
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setSelectedTaskId = useCallback((nextSelectedTaskId: string | null) => {
    setTaskState(current => ({
      ...current,
      selectedTaskId: nextSelectedTaskId,
    }));
  }, []);

  const listColumns = useMemo(
    () => [
      {
        header: { label: 'Extraction Task' },
        cell: {
          content: (task: DocumentExtractionV2Invocation) => (
            <Box direction="column">
              <Box direction="row" justify="space-between" gap={1}>
                <Text size="table">{task.task_id}</Text>
                <Text
                  size="table"
                  color="default"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {formatDocumentExtractionV2Status(task.status)}
                </Text>
              </Box>
              <Text size="table" color="default">
                {formatDateTime(task.created)}
              </Text>
            </Box>
          ),
        },
      },
    ],
    [],
  );

  const loadTasks = useCallback(async () => {
    if (!subjectId || !projectId) {
      return;
    }
    setIsLoadingTasks(true);
    try {
      const response = await apiClient.request(
        'GET /v1/agents-v2/document-extraction-v2/tasks',
        {
          subject_id: subjectId,
          project_id: projectId,
          limit: 25,
        },
      );
      const nextTasks = response.data.tasks;
      setTaskState(current => {
        if (
          current.selectedTaskId &&
          nextTasks.some(task => task.task_id === current.selectedTaskId)
        ) {
          return {
            tasks: nextTasks,
            selectedTaskId: current.selectedTaskId,
          };
        }
        const sourceTask = highlightedSourceDocumentReferenceId
          ? nextTasks.find(task => {
              const sourceResource = getTaskSourceResource(task);
              return (
                sourceResource?.resourceType === 'DocumentReference' &&
                sourceResource.id === highlightedSourceDocumentReferenceId
              );
            })
          : undefined;
        return {
          tasks: nextTasks,
          selectedTaskId: sourceTask?.task_id ?? nextTasks[0]?.task_id ?? null,
        };
      });
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(
        formatMessage(memberDetailMessages.memberDocumentsExtractionLoadError),
      );
    } finally {
      setIsLoadingTasks(false);
    }
  }, [
    apiClient,
    formatMessage,
    highlightedSourceDocumentReferenceId,
    projectId,
    subjectId,
  ]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!tasks.some(isDocumentExtractionV2InFlight)) {
      return;
    }
    const interval = window.setInterval(loadTasks, 3000);
    return () => window.clearInterval(interval);
  }, [loadTasks, tasks]);

  return (
    <Box direction="column" gap={1} style={{ height: '100%', minHeight: 0 }}>
      {errorMessage ? <Text color="negative">{errorMessage}</Text> : null}
      <AuditMasterDetail
        rows={tasks}
        loading={isLoadingTasks && tasks.length === 0}
        emptyMessage={formatMessage(
          memberDetailMessages.memberDocumentsExtractionNoTasks,
        )}
        noSelectionMessage={formatMessage(
          memberDetailMessages.memberDocumentsExtractionSelectTask,
        )}
        rowId={task => task.task_id}
        listColumns={listColumns}
        detailTitle={task => task.task_id}
        renderDetail={task => (
          <DocumentExtractionTaskDetail
            task={task}
            subjectId={subjectId}
            highlightedSourceDocumentReferenceId={
              highlightedSourceDocumentReferenceId
            }
          />
        )}
        selectedRowId={selectedTaskId}
        setSelectedRowId={setSelectedTaskId}
      />
    </Box>
  );
};
